import {
  Button,
  Form,
  XStack,
  Error,
  InputFieldRhf,
  RadioGroupFieldRhf,
  YStack,
  SizableText,
  Separator,
  Label,
  RadioGroup,
  Stack,
} from '@my/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { GroupCreationHeader } from './group-creation-header'
import { UserAvatar } from 'app/features/user/components'
import { User } from 'app/types'
import { Globe2, Lock } from '@tamagui/lucide-icons'

type RadioGroupItemProps = {
  value: 'public' | 'private'
  name: string
  helperText?: string
}

const iconMap = {
  public: Globe2,
  private: Lock,
}

const RadioGroupItem = ({ value, name, helperText }: RadioGroupItemProps) => {
  const id = `radiogroup-${value}`
  const Icon = iconMap[value]
  return (
    <XStack
      jc="space-between"
      ai="center"
      gap="$2"
    >
      <XStack fd="row" flexGrow={1} flexShrink={1} maxHeight={60} ai="center">
        <Stack w={56} h={56} br={1000} bg="$overlay2" jc="center" ai="center">
          <Icon size={16} />
        </Stack>
        <YStack flexShrink={1} pl="$2">
          <Label
            unstyled
            htmlFor={id}
            numberOfLines={1}
            fontSize="$4"
            fow="400"
            height="auto"
            cursor="pointer"
          >
            {name}
          </Label>
          <SizableText color="$overlay10" size="$1">
            {helperText}
          </SizableText>
        </YStack>
      </XStack>

      <RadioGroup.Item flexShrink={0} value={value} id={id} size="$4" cursor="pointer">
        <RadioGroup.Indicator />
      </RadioGroup.Item>
    </XStack>
  )
}

const alphanumericRegex = /^[A-Za-z0-9\s]+$/
const formSchema = z.object({
  name: z
    .string()
    .min(3, 'Name should be at least 3 characters')
    .max(50, 'Name should be at most 50 characters')
    .refine((value) => alphanumericRegex.test(value), 'Name can only contain letters and numbers'),
  visibility: z.string().min(1, 'Visibility is required'),
})

export type GroupCreationFormDetailsValues = z.infer<typeof formSchema>

export interface GroupCreationFormProps {
  error?: string
  user: Pick<User, 'fullName' | 'avatar'>
  defaultValues?: Partial<GroupCreationFormDetailsValues>
  onSubmit: (values: GroupCreationFormDetailsValues) => void
  onClose: () => void
}

export const GroupCreationFormDetails = (props: GroupCreationFormProps) => {
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      visibility: '',
      ...props.defaultValues,
    },
  })

  return (
    <Form
      gap="$3.5"
      w="100%"
      flex={1}
      onSubmit={handleSubmit((data) => {
        props.onSubmit(data)
      })}
    >
      <GroupCreationHeader title="Create Group" onClose={props.onClose}>
        <Form.Trigger asChild fd="row" ai="center">
          <Button size="$3" theme="primary">
            Next
          </Button>
        </Form.Trigger>
      </GroupCreationHeader>

      <XStack w="100%" gap="$3.5" ai="center">
        <UserAvatar src={props.user?.avatar?.small} width={44} height={44} />
        <YStack flex={1}>
          <SizableText size="$3" fow="500">
            {props.user?.fullName}
          </SizableText>
        </YStack>
      </XStack>

      <Separator boc="$overlay8" w="100%" />

      <InputFieldRhf
        name="name"
        label="Name"
        hideLabel
        placeholder="Name of your Group"
        fullWidth
        shadowOpacity={0}
        showHelperText
        control={control}
        p="$3.5"
        h={56}
        boc="$gray4"
        hoverStyle={{
          boc: '$gray6',
        }}
        focusStyle={{
          boc: '$gray7',
          outlineWidth: 0,
        }}
      />

      <RadioGroupFieldRhf
        name="visibility"
        control={control}
        label="Who can see your posts?"
        gap="$3"
        labelProps={{
          fow: '500',
          fontSize: '$5',
        }}
      >
        <YStack jc="center" gap="$3.5">
          <RadioGroupItem value="public" name="Public" helperText="Anyone can join" />
          <RadioGroupItem
            value="private"
            name="Private"
            helperText="Only people with an invite can join"
          />
        </YStack>
      </RadioGroupFieldRhf>

      {props.error && <Error error={props.error} />}
    </Form>
  )
}
